/**
 * Topology-op executor — the shared snapshot-restore + geometry-call +
 * scene-patch pipeline used by parameterized topology commands. This is the
 * single replay path for live preview, confirm, and Adjust Last Operation.
 */

import {
  clearEditSelection,
  deriveVerticesFromFaces,
} from "../../modeling/editSelection.ts";
import {
  computeSelectionNormal,
  bevelGeometry as meshBevelGeometry,
  bridgeEdgeLoops as meshBridgeEdgeLoops,
  extrudeEdges as meshExtrudeEdges,
  extrudeFaces as meshExtrudeFaces,
  extrudeIndividualFaces as meshExtrudeIndividualFaces,
  extrudeManifold as meshExtrudeManifold,
  extrudeVertices as meshExtrudeVertices,
  gridFill as meshGridFill,
  insertEdgeLoop as meshInsertEdgeLoop,
  insetFaces as meshInsetFaces,
  mergeByDistance as meshMergeByDistance,
  subdivide as meshSubdivide,
  type VertexProvenance,
} from "../../modeling/meshOps.ts";
import {
  getCommandMetadata,
  usesTopologyPreview,
} from "../../modeling/operatorParams.ts";
import { cloneTopology } from "../../modeling/topology.ts";
import {
  preflightBevelSelection,
  preflightBridgeEdgeLoops,
  preflightEdgeLoopInsertSeed,
  preflightExtrudeManifold,
  preflightExtrudeSelection,
  preflightGridFillSelection,
  preflightInsetSelection,
  resolveBoundarySeedVertex,
  resolveSelectedEdgeSeed,
  type TopologySelectionInput,
} from "../../modeling/topologyOpPreflight.ts";
import type {
  LoopNormals,
  PolygonTopology,
  RenderTriangleIndices,
  SceneObject,
  VertexNormals,
} from "../../modeling/types.ts";
import { loopToVertexNormals } from "../../wasm/sculpt.ts";
import {
  clearLiveEditPreview,
  patchLiveEditPreview,
} from "../liveEditPreview.ts";
import {
  applyResult,
  replaceSceneObjectAfterEdit,
} from "../storeEditOps/index.ts";
import type { AppState, CommandType } from "../storeTypes.ts";

type Set_ = (partial: Partial<AppState>) => void;

export interface TopologyExecutionSuccess {
  ok: true;
  previewSeed?: TopologyPreviewSeed;
}

export interface TopologyExecutionFailure {
  ok: false;
  reason: string;
}

export type TopologyExecutionResult =
  | TopologyExecutionSuccess
  | TopologyExecutionFailure;

const TOPOLOGY_EXECUTION_OK: TopologyExecutionSuccess = { ok: true };

type SnapObj = SceneObject;

interface TopologyPreviewSeedBase {
  type: CommandType;
  /** Full parameter identity for seeds whose topology and positions are final. */
  paramsKey: string;
  /** Parameter identity for topology structure, excluding shape-only values. */
  structuralKey: string;
  vertices: Float32Array;
  topology: PolygonTopology;
  indices: RenderTriangleIndices;
  loopNormals: LoopNormals;
  uvs?: Float32Array;
  selectedVertices: Set<number>;
  selectedFaces?: Set<number>;
}

export interface StaticTopologyPreviewSeed extends TopologyPreviewSeedBase {
  kind: "staticResult";
}

export interface LinearTopologyPreviewSeed extends TopologyPreviewSeedBase {
  kind: "linearDeformation";
  selectedVertexOrder: Uint32Array;
  directionVectors: Float32Array;
}

export interface AffineTopologyPreviewSeed extends TopologyPreviewSeedBase {
  kind: "affineDeformation";
  parameterKeys: string[];
  selectedVertexOrder: Uint32Array;
  directionVectorsByParam: Record<string, Float32Array>;
  baseValuesByParam?: Record<string, number>;
  maxValuesByParam?: Record<string, number>;
}

export type TopologyPreviewSeed =
  | StaticTopologyPreviewSeed
  | LinearTopologyPreviewSeed
  | AffineTopologyPreviewSeed;

export interface FrozenTopologySelectionState {
  preEditFaces: Set<number> | null;
  preEditSelection: Set<number> | null;
  preEditEdges: Set<number> | null;
  preEditActiveVertex: number | null;
  preEditActiveEdge: number | null;
  preEditActiveFace: number | null;
  edgeLoopInsertEdge: [number, number] | null;
}

type TopologyExecutionMode = "preview" | "commit";

interface TopologyStrategyContext {
  type: CommandType;
  params: Record<string, number | boolean | string>;
  srcVerts: Float32Array;
  snapObj: SnapObj;
  em: NonNullable<AppState["editMode"]>;
  obj: SceneObject;
  state: AppState;
  set_: Set_;
  frozen: FrozenTopologySelectionState;
  mode: TopologyExecutionMode;
  previewSeed: TopologyPreviewSeed | null;
  getSnapTopology: () => PolygonTopology;
  selection: TopologySelectionInput;
}

interface TopologyPreviewStrategy {
  previewCadenceMs: number;
  execute(ctx: TopologyStrategyContext): TopologyExecutionResult;
}

function buildSelectionInput(
  em: NonNullable<AppState["editMode"]>,
  frozen: FrozenTopologySelectionState,
): TopologySelectionInput {
  return {
    granularity: em.granularity,
    selectedVertices: frozen.preEditSelection ?? em.selectedVertices,
    selectedEdges: frozen.preEditEdges,
    selectedFaces: frozen.preEditFaces,
    lastActiveVertex: frozen.preEditActiveVertex,
    lastActiveEdge: frozen.preEditActiveEdge,
    lastActiveFace: frozen.preEditActiveFace,
  };
}

function selectionPatchFromResult(
  type: CommandType,
  em: NonNullable<AppState["editMode"]>,
  topology: PolygonTopology,
  newSelection: Set<number>,
  newSelectedFaces?: Set<number>,
): NonNullable<AppState["editMode"]> {
  const cleared = clearEditSelection(em);
  const meta = getCommandMetadata(type);
  if (meta.outputSelection === "preserve") return cleared;

  if (
    (meta.outputSelection === "newFacesAndVertices" ||
      meta.outputSelection === "newFacesAndVerticesWhenAvailable") &&
    newSelectedFaces &&
    newSelectedFaces.size > 0
  ) {
    return {
      ...cleared,
      selectedFaces: newSelectedFaces,
      selectedVertices: deriveVerticesFromFaces(topology, newSelectedFaces),
    };
  }

  return {
    ...cleared,
    selectedVertices: newSelection,
  };
}

function applyTopologyResult(
  type: CommandType,
  result: {
    vertices: Float32Array;
    indices: RenderTriangleIndices;
    topology: PolygonTopology;
    newSelection: Set<number>;
    newSelectedFaces?: Set<number>;
    provenance: {
      remap: Int32Array;
      parentOffsets: Uint32Array;
      parents: Uint32Array;
    };
  },
  em: NonNullable<AppState["editMode"]>,
  obj: SceneObject,
  state: AppState,
  set_: Set_,
  mode: "preview" | "commit",
): TopologyExecutionResult {
  const newTopology = result.topology ?? obj.topology;
  const newSelectedFaces = result.newSelectedFaces
    ? new Set(result.newSelectedFaces)
    : undefined;
  const nextEditMode = selectionPatchFromResult(
    type,
    em,
    newTopology,
    result.newSelection,
    newSelectedFaces,
  );
  if (mode === "preview") {
    const previewObj = applyResult(obj, result, type);
    set_({
      activeEditSession: {
        objectId: em.objectId,
        vertices: previewObj.vertices,
        topology: previewObj.topology,
        indices: previewObj.indices,
        loopNormals: previewObj.loopNormals,
        uvs: previewObj.uvs,
        skinRadii: previewObj.skinRadii,
        baseVertices: previewObj.baseVertices,
        dirtyVertices: null,
        vertexPatch: null,
      },
    });
    return TOPOLOGY_EXECUTION_OK;
  }
  clearLiveEditPreview(em.objectId);
  set_({
    ...replaceSceneObjectAfterEdit(
      state,
      em.objectId,
      applyResult(obj, result, type),
    ),
    activeEditSession: null,
    editMode: nextEditMode,
  });
  return TOPOLOGY_EXECUTION_OK;
}

function offsetSelectedVertices(
  vertices: Float32Array,
  selectedVertices: ReadonlySet<number>,
  direction: [number, number, number],
  offset: number,
): void {
  if (offset === 0) return;
  const [dx, dy, dz] = direction;
  for (const vi of selectedVertices) {
    vertices[vi * 3] += dx * offset;
    vertices[vi * 3 + 1] += dy * offset;
    vertices[vi * 3 + 2] += dz * offset;
  }
}

function offsetAlongVertexNormals(
  vertices: Float32Array,
  selectedVertices: ReadonlySet<number>,
  vertexNormals: VertexNormals,
  provenance: VertexProvenance,
  offset: number,
): void {
  if (offset === 0) return;
  for (const newV of selectedVertices) {
    const pStart = provenance.parentOffsets[newV];
    const pEnd = provenance.parentOffsets[newV + 1];
    if (pEnd <= pStart) continue;
    const oldV = provenance.parents[pStart];
    vertices[newV * 3] += vertexNormals[oldV * 3] * offset;
    vertices[newV * 3 + 1] += vertexNormals[oldV * 3 + 1] * offset;
    vertices[newV * 3 + 2] += vertexNormals[oldV * 3 + 2] * offset;
  }
}

function offsetIndividualFacesAlongNormals(
  vertices: Float32Array,
  topology: PolygonTopology,
  newSelectedFaces: Set<number>,
  newSelection: Set<number>,
  offset: number,
): void {
  if (offset === 0) return;
  const { offsets, corners } = topology;
  for (const fi of newSelectedFaces) {
    const start = offsets[fi];
    const size = offsets[fi + 1] - offsets[fi];
    if (size < 3) continue;
    const v0 = corners[start] * 3,
      v1 = corners[start + 1] * 3,
      v2 = corners[start + 2] * 3;
    const e1x = vertices[v1] - vertices[v0],
      e1y = vertices[v1 + 1] - vertices[v0 + 1],
      e1z = vertices[v1 + 2] - vertices[v0 + 2];
    const e2x = vertices[v2] - vertices[v0],
      e2y = vertices[v2 + 1] - vertices[v0 + 1],
      e2z = vertices[v2 + 2] - vertices[v0 + 2];
    let nx = e1y * e2z - e1z * e2y,
      ny = e1z * e2x - e1x * e2z,
      nz = e1x * e2y - e1y * e2x;
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len < 1e-8) continue;
    nx /= len;
    ny /= len;
    nz /= len;
    for (let j = 0; j < size; j++) {
      const vi = corners[start + j];
      if (!newSelection.has(vi)) continue;
      vertices[vi * 3] += nx * offset;
      vertices[vi * 3 + 1] += ny * offset;
      vertices[vi * 3 + 2] += nz * offset;
    }
  }
}

function buildDirectionalPreviewData(
  selectedVertices: ReadonlySet<number>,
  resolveDirection: (vertexIndex: number) => [number, number, number] | null,
): {
  selectedVertexOrder: Uint32Array;
  directionVectors: Float32Array;
} {
  const vertexOrder = new Uint32Array(selectedVertices.size);
  const directionVectors = new Float32Array(selectedVertices.size * 3);
  let cursor = 0;
  for (const vi of selectedVertices) {
    vertexOrder[cursor] = vi;
    const direction = resolveDirection(vi) ?? [0, 0, 0];
    directionVectors[cursor * 3] = direction[0];
    directionVectors[cursor * 3 + 1] = direction[1];
    directionVectors[cursor * 3 + 2] = direction[2];
    cursor += 1;
  }
  return { selectedVertexOrder: vertexOrder, directionVectors };
}

function topologyParamsKey(
  type: CommandType,
  params: Record<string, number | boolean | string>,
  omit: ReadonlySet<string> = new Set(),
): string {
  const parts = Object.keys(params)
    .filter((key) => !omit.has(key))
    .sort()
    .map((key) => `${key}:${String(params[key])}`);
  return `${type}|${parts.join("|")}`;
}

function sameStaticPreviewSeed(
  seed: TopologyPreviewSeed | null,
  type: CommandType,
  key: string,
): seed is StaticTopologyPreviewSeed {
  return (
    !!seed &&
    seed.type === type &&
    seed.kind === "staticResult" &&
    seed.paramsKey === key
  );
}

function sameLinearPreviewSeed(
  seed: TopologyPreviewSeed | null,
  type: CommandType,
  structuralKey: string,
): seed is LinearTopologyPreviewSeed {
  return (
    !!seed &&
    seed.type === type &&
    seed.kind === "linearDeformation" &&
    seed.structuralKey === structuralKey
  );
}

function sameAffinePreviewSeed(
  seed: TopologyPreviewSeed | null,
  type: CommandType,
  structuralKey: string,
): seed is AffineTopologyPreviewSeed {
  return (
    !!seed &&
    seed.type === type &&
    seed.kind === "affineDeformation" &&
    seed.structuralKey === structuralKey
  );
}

function buildDirectionalVertexPatch(
  seed: LinearTopologyPreviewSeed,
  offset: number,
): { indices: Uint32Array; positions: Float32Array } {
  const { selectedVertexOrder, directionVectors, vertices } = seed;
  const positions = new Float32Array(selectedVertexOrder.length * 3);
  for (let i = 0; i < selectedVertexOrder.length; i++) {
    const vi = selectedVertexOrder[i]!;
    const src = vi * 3;
    const dst = i * 3;
    const dir = i * 3;
    positions[dst] = vertices[src]! + directionVectors[dir]! * offset;
    positions[dst + 1] =
      vertices[src + 1]! + directionVectors[dir + 1]! * offset;
    positions[dst + 2] =
      vertices[src + 2]! + directionVectors[dir + 2]! * offset;
  }
  return { indices: selectedVertexOrder, positions };
}

function buildAffineVertexPatch(
  seed: AffineTopologyPreviewSeed,
  values: Record<string, number>,
): { indices: Uint32Array; positions: Float32Array } {
  const { selectedVertexOrder, directionVectorsByParam, vertices } = seed;
  const positions = new Float32Array(selectedVertexOrder.length * 3);
  for (let i = 0; i < selectedVertexOrder.length; i++) {
    const vi = selectedVertexOrder[i]!;
    const src = vi * 3;
    const dst = i * 3;
    let x = vertices[src]!;
    let y = vertices[src + 1]!;
    let z = vertices[src + 2]!;
    for (const key of seed.parameterKeys) {
      const value = (values[key] ?? 0) - (seed.baseValuesByParam?.[key] ?? 0);
      if (value === 0) continue;
      const vectors = directionVectorsByParam[key];
      if (!vectors) continue;
      x += vectors[dst]! * value;
      y += vectors[dst + 1]! * value;
      z += vectors[dst + 2]! * value;
    }
    positions[dst] = x;
    positions[dst + 1] = y;
    positions[dst + 2] = z;
  }
  return { indices: selectedVertexOrder, positions };
}

function buildAffineComponentData(
  baseVertices: Float32Array,
  components: {
    key: string;
    vertices: Float32Array;
    basisValue: number;
  }[],
): Pick<
  AffineTopologyPreviewSeed,
  "parameterKeys" | "selectedVertexOrder" | "directionVectorsByParam"
> | null {
  if (components.length === 0) return null;
  const vertexCount = baseVertices.length / 3;
  if (!Number.isInteger(vertexCount)) return null;
  for (const component of components) {
    if (
      component.basisValue === 0 ||
      component.vertices.length !== baseVertices.length
    ) {
      return null;
    }
  }

  const dirty: number[] = [];
  const epsilon = 1e-8;
  for (let vi = 0; vi < vertexCount; vi++) {
    const src = vi * 3;
    let changed = false;
    for (const component of components) {
      const inv = 1 / component.basisValue;
      const dx = (component.vertices[src]! - baseVertices[src]!) * inv;
      const dy = (component.vertices[src + 1]! - baseVertices[src + 1]!) * inv;
      const dz = (component.vertices[src + 2]! - baseVertices[src + 2]!) * inv;
      if (
        Math.abs(dx) > epsilon ||
        Math.abs(dy) > epsilon ||
        Math.abs(dz) > epsilon
      ) {
        changed = true;
        break;
      }
    }
    if (changed) dirty.push(vi);
  }

  const selectedVertexOrder = Uint32Array.from(dirty);
  const directionVectorsByParam: Record<string, Float32Array> = {};
  for (const component of components) {
    const vectors = new Float32Array(selectedVertexOrder.length * 3);
    const inv = 1 / component.basisValue;
    for (let i = 0; i < selectedVertexOrder.length; i++) {
      const vi = selectedVertexOrder[i]!;
      const src = vi * 3;
      const dst = i * 3;
      vectors[dst] = (component.vertices[src]! - baseVertices[src]!) * inv;
      vectors[dst + 1] =
        (component.vertices[src + 1]! - baseVertices[src + 1]!) * inv;
      vectors[dst + 2] =
        (component.vertices[src + 2]! - baseVertices[src + 2]!) * inv;
    }
    directionVectorsByParam[component.key] = vectors;
  }

  return {
    parameterKeys: components.map((component) => component.key),
    selectedVertexOrder,
    directionVectorsByParam,
  };
}

function hasCompatibleAffineResult(
  base: { vertices: Float32Array; topology: PolygonTopology },
  component: { vertices: Float32Array; topology: PolygonTopology },
): boolean {
  if (base.vertices.length !== component.vertices.length) return false;
  const a = base.topology;
  const b = component.topology;
  if (a.offsets.length !== b.offsets.length) return false;
  if (a.corners.length !== b.corners.length) return false;
  for (let i = 0; i < a.offsets.length; i++) {
    if (a.offsets[i] !== b.offsets[i]) return false;
  }
  for (let i = 0; i < a.corners.length; i++) {
    if (a.corners[i] !== b.corners[i]) return false;
  }
  return true;
}

function buildIndividualExtrudePreviewData(
  vertices: Float32Array,
  topology: PolygonTopology,
  selectedFaces: Set<number>,
  selectedVertices: Set<number>,
): {
  selectedVertexOrder: Uint32Array;
  directionVectors: Float32Array;
} {
  const faceDirections = new Map<number, [number, number, number]>();
  const { offsets, corners } = topology;
  for (const fi of selectedFaces) {
    const start = offsets[fi];
    const size = offsets[fi + 1] - start;
    if (size < 3) continue;
    const v0 = corners[start] * 3;
    const v1 = corners[start + 1] * 3;
    const v2 = corners[start + 2] * 3;
    const e1x = vertices[v1] - vertices[v0];
    const e1y = vertices[v1 + 1] - vertices[v0 + 1];
    const e1z = vertices[v1 + 2] - vertices[v0 + 2];
    const e2x = vertices[v2] - vertices[v0];
    const e2y = vertices[v2 + 1] - vertices[v0 + 1];
    const e2z = vertices[v2 + 2] - vertices[v0 + 2];
    let nx = e1y * e2z - e1z * e2y;
    let ny = e1z * e2x - e1x * e2z;
    let nz = e1x * e2y - e1y * e2x;
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
    if (len < 1e-8) continue;
    nx /= len;
    ny /= len;
    nz /= len;
    for (let j = 0; j < size; j++) {
      const vi = corners[start + j];
      if (!selectedVertices.has(vi) || faceDirections.has(vi)) continue;
      faceDirections.set(vi, [nx, ny, nz]);
    }
  }
  return buildDirectionalPreviewData(
    selectedVertices,
    (vi) => faceDirections.get(vi) ?? null,
  );
}

function applyPreviewSeed(
  seed: TopologyPreviewSeed,
  em: NonNullable<AppState["editMode"]>,
  set_: Set_,
  vertexPatch?: { indices: Uint32Array; positions: Float32Array } | null,
): TopologyExecutionSuccess {
  setPreviewSession(
    set_,
    em.objectId,
    {
      vertices: seed.vertices,
      topology: seed.topology,
      indices: seed.indices,
      loopNormals: seed.loopNormals,
      uvs: seed.uvs,
      dirtyVertices: vertexPatch?.indices ?? null,
      vertexPatch: vertexPatch ?? null,
    },
  );
  return TOPOLOGY_EXECUTION_OK;
}

function applyLinearPreviewSeed(
  seed: LinearTopologyPreviewSeed,
  offset: number,
  em: NonNullable<AppState["editMode"]>,
  set_: Set_,
): TopologyExecutionSuccess {
  return applyPreviewSeed(
    seed,
    em,
    set_,
    buildDirectionalVertexPatch(seed, offset),
  );
}

function applyAffinePreviewSeed(
  seed: AffineTopologyPreviewSeed,
  values: Record<string, number>,
  em: NonNullable<AppState["editMode"]>,
  set_: Set_,
): TopologyExecutionSuccess {
  return applyPreviewSeed(seed, em, set_, buildAffineVertexPatch(seed, values));
}

function patchLinearPreviewSeed(
  objectId: string,
  seed: LinearTopologyPreviewSeed,
  offset: number,
): TopologyExecutionSuccess {
  const vertexPatch = buildDirectionalVertexPatch(seed, offset);
  patchLiveEditPreview(objectId, {
    dirtyVertices: vertexPatch.indices,
    vertexPatch,
  });
  return TOPOLOGY_EXECUTION_OK;
}

function patchAffinePreviewSeed(
  objectId: string,
  seed: AffineTopologyPreviewSeed,
  values: Record<string, number>,
): TopologyExecutionSuccess {
  const vertexPatch = buildAffineVertexPatch(seed, values);
  patchLiveEditPreview(objectId, {
    dirtyVertices: vertexPatch.indices,
    vertexPatch,
  });
  return TOPOLOGY_EXECUTION_OK;
}

function applyCachedTopologyResult(
  ctx: TopologyStrategyContext,
  result: {
    vertices: Float32Array;
    indices: RenderTriangleIndices;
    topology: PolygonTopology;
    newSelection: Set<number>;
    newSelectedFaces?: Set<number>;
    provenance: VertexProvenance;
  },
  paramsKey: string,
): TopologyExecutionResult {
  if (
    ctx.mode === "preview" &&
    sameStaticPreviewSeed(ctx.previewSeed, ctx.type, paramsKey)
  ) {
    return applyPreviewSeed(ctx.previewSeed, ctx.em, ctx.set_);
  }

  if (ctx.mode !== "preview") {
    return applyTopologyResult(
      ctx.type,
      result,
      ctx.em,
      ctx.obj,
      ctx.state,
      ctx.set_,
      ctx.mode,
    );
  }

  const previewObj = applyResult(ctx.obj, result, ctx.type);
  const seed: StaticTopologyPreviewSeed = {
    kind: "staticResult",
    type: ctx.type,
    paramsKey,
    structuralKey: paramsKey,
    vertices: previewObj.vertices,
    topology: previewObj.topology,
    indices: previewObj.indices,
    loopNormals: previewObj.loopNormals,
    ...(previewObj.uvs ? { uvs: previewObj.uvs } : {}),
    selectedVertices: result.newSelection,
    ...(result.newSelectedFaces
      ? { selectedFaces: new Set(result.newSelectedFaces) }
      : {}),
  };
  applyPreviewSeed(seed, ctx.em, ctx.set_);
  return { ok: true, previewSeed: seed };
}

function applyReusableStaticPreview(
  ctx: TopologyStrategyContext,
  paramsKey: string,
): TopologyExecutionSuccess | null {
  if (
    ctx.mode === "preview" &&
    sameStaticPreviewSeed(ctx.previewSeed, ctx.type, paramsKey)
  ) {
    return applyPreviewSeed(ctx.previewSeed, ctx.em, ctx.set_);
  }
  return null;
}

function executeSeededDeformationStrategy(
  ctx: TopologyStrategyContext & {
    structuralKey: string;
    offset: number;
    buildResult: () =>
      | ReturnType<typeof meshExtrudeFaces>
      | ReturnType<typeof meshExtrudeEdges>
      | ReturnType<typeof meshExtrudeVertices>
      | ReturnType<typeof meshExtrudeIndividualFaces>;
    buildSeed: (
      result: NonNullable<
        | ReturnType<typeof meshExtrudeFaces>
        | ReturnType<typeof meshExtrudeEdges>
        | ReturnType<typeof meshExtrudeVertices>
        | ReturnType<typeof meshExtrudeIndividualFaces>
      >,
      seededPreview: SceneObject,
    ) => LinearTopologyPreviewSeed;
    applyCommitOffset: (
      result: NonNullable<
        | ReturnType<typeof meshExtrudeFaces>
        | ReturnType<typeof meshExtrudeEdges>
        | ReturnType<typeof meshExtrudeVertices>
        | ReturnType<typeof meshExtrudeIndividualFaces>
      >,
    ) => void;
    failureReason: string;
  },
): TopologyExecutionResult {
  if (
    ctx.mode === "preview" &&
    sameLinearPreviewSeed(ctx.previewSeed, ctx.type, ctx.structuralKey)
  ) {
    return patchLinearPreviewSeed(ctx.em.objectId, ctx.previewSeed, ctx.offset);
  }

  const result = ctx.buildResult();
  if (!result) {
    return { ok: false, reason: ctx.failureReason };
  }

  if (ctx.mode === "preview") {
    const seededPreview = applyResult(ctx.obj, result, ctx.type);
    const seed = ctx.buildSeed(result, seededPreview);
    applyLinearPreviewSeed(seed, ctx.offset, ctx.em, ctx.set_);
    return { ok: true, previewSeed: seed };
  }

  ctx.applyCommitOffset(result);
  return applyTopologyResult(
    ctx.type,
    result,
    ctx.em,
    ctx.obj,
    ctx.state,
    ctx.set_,
    ctx.mode,
  );
}

function setPreviewSession(
  set_: Set_,
  objectId: string,
  patch: {
    vertices: Float32Array;
    topology: PolygonTopology;
    indices: RenderTriangleIndices;
    loopNormals: LoopNormals;
    uvs?: Float32Array;
    dirtyVertices?: Uint32Array | null;
    vertexPatch?: { indices: Uint32Array; positions: Float32Array } | null;
  },
): void {
  set_({
    activeEditSession: {
      objectId,
      vertices: patch.vertices,
      topology: patch.topology,
      indices: patch.indices,
      loopNormals: patch.loopNormals,
      ...(patch.uvs ? { uvs: patch.uvs } : {}),
      dirtyVertices: patch.dirtyVertices ?? null,
      vertexPatch: patch.vertexPatch ?? null,
    },
  });
  if (patch.vertexPatch) {
    patchLiveEditPreview(objectId, {
      dirtyVertices: patch.dirtyVertices ?? patch.vertexPatch.indices,
      vertexPatch: patch.vertexPatch,
    });
  } else {
    clearLiveEditPreview(objectId);
  }
}

function executeSubdivideCuts(
  srcVerts: Float32Array,
  srcIndices: RenderTriangleIndices,
  topology: PolygonTopology,
  selected: Set<number>,
  cuts: number,
): ReturnType<typeof meshSubdivide> {
  let currentVertices = srcVerts;
  let currentIndices = srcIndices;
  let currentTopology = topology;
  let currentSelection = new Set(selected);
  let result: ReturnType<typeof meshSubdivide> = null;

  for (let i = 0; i < cuts; i++) {
    result = meshSubdivide(
      currentVertices,
      currentIndices,
      currentTopology,
      currentSelection,
    );
    if (!result) return null;
    currentVertices = result.vertices;
    currentIndices = result.indices;
    currentTopology = result.topology;
    currentSelection = result.newSelection;
  }

  return result;
}

// ---------------------------------------------------------------------------
// executeTopologyOp — shared snapshot-restore + geometry-call + scene-patch
// ---------------------------------------------------------------------------

/**
 * Return a fresh `PolygonTopology` whose buffers are detached from the
 * snapshot. Topology ops mutate their input, so we can't hand them the
 * snapshot's arrays directly — that would corrupt the redo entry.
 */
export function snapToTopology(snapObj: SceneObject): PolygonTopology {
  const t = snapObj.topology;
  if (import.meta.env.DEV) {
    if (t.offsets.length < 1) {
      throw new Error("snapToTopology: offsets array is empty in snapshot");
    }
    if (t.offsets[0] !== 0) {
      throw new Error(
        `snapToTopology: offsets[0] = ${t.offsets[0]}, expected 0`,
      );
    }
    if (t.offsets[t.offsets.length - 1] !== t.corners.length) {
      throw new Error(
        `snapToTopology: last offset ${t.offsets[t.offsets.length - 1]} !== corners.length ${t.corners.length}`,
      );
    }
  }
  return cloneTopology(t);
}

/**
 * Per-vertex normals derived from a snapshot's per-corner loop normals, cached
 * by reference identity. The snapshot is frozen for the duration of a modal
 * command so the cache naturally persists across drag frames and auto-
 * invalidates when a new command starts with a fresh snapshot.
 */
let _snapshotVertexNormalsKey: LoopNormals | null = null;
let _snapshotVertexNormalsValue: VertexNormals | null = null;

export function getSnapshotVertexNormals(snapObj: SceneObject): VertexNormals {
  if (
    _snapshotVertexNormalsKey === snapObj.loopNormals &&
    _snapshotVertexNormalsValue
  ) {
    return _snapshotVertexNormalsValue;
  }
  const normals = loopToVertexNormals(
    snapObj.loopNormals,
    snapObj.topology,
    snapObj.vertices.length / 3,
  );
  _snapshotVertexNormalsKey = snapObj.loopNormals;
  _snapshotVertexNormalsValue = normals;
  return normals;
}

function edgeKey(a: number, b: number): string {
  return a < b ? `${a},${b}` : `${b},${a}`;
}

function topologyEdgeKey(topology: PolygonTopology, edgeIndex: number): string {
  const a = topology.edges[edgeIndex * 2]!;
  const b = topology.edges[edgeIndex * 2 + 1]!;
  return edgeKey(a, b);
}

function vertexAt(vertices: Float32Array, v: number): [number, number, number] {
  const i = v * 3;
  return [vertices[i]!, vertices[i + 1]!, vertices[i + 2]!];
}

function sub3n(
  a: [number, number, number],
  b: [number, number, number],
): [number, number, number] {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function dot3n(
  a: [number, number, number],
  b: [number, number, number],
): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function length3n(v: [number, number, number]): number {
  return Math.sqrt(dot3n(v, v));
}

function normalize3n(v: [number, number, number]): [number, number, number] {
  const len = length3n(v);
  return len > 1e-6 ? [v[0] / len, v[1] / len, v[2] / len] : [0, 0, 0];
}

function computeFaceNormals(
  vertices: Float32Array,
  topology: PolygonTopology,
): Float32Array {
  const faceCount = topology.offsets.length - 1;
  const normals = new Float32Array(faceCount * 3);
  const { offsets, corners } = topology;
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f]!;
    const size = offsets[f + 1]! - start;
    let nx = 0;
    let ny = 0;
    let nz = 0;
    for (let j = 0; j < size; j++) {
      const a = vertexAt(vertices, corners[start + j]!);
      const b = vertexAt(vertices, corners[start + ((j + 1) % size)]!);
      nx += (a[1] - b[1]) * (a[2] + b[2]);
      ny += (a[2] - b[2]) * (a[0] + b[0]);
      nz += (a[0] - b[0]) * (a[1] + b[1]);
    }
    const normalized = normalize3n([nx, ny, nz]);
    normals[f * 3] = normalized[0];
    normals[f * 3 + 1] = normalized[1];
    normals[f * 3 + 2] = normalized[2];
  }
  return normals;
}

function buildEdgeToFaces(topology: PolygonTopology): Map<string, number[]> {
  const out = new Map<string, number[]>();
  const { offsets, corners, cornerEdges, edges } = topology;
  const faceCount = offsets.length - 1;
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f]!;
    const size = offsets[f + 1]! - start;
    for (let j = 0; j < size; j++) {
      const cornerIndex = start + j;
      const edgeIndex = cornerEdges[cornerIndex]!;
      const a = edges[edgeIndex * 2]!;
      const b = edges[edgeIndex * 2 + 1]!;
      const key = edgeKey(a, b);
      let faces = out.get(key);
      if (!faces) {
        faces = [];
        out.set(key, faces);
      }
      if (!faces.includes(f)) faces.push(f);
      // Keep `corners` touched in this loop so malformed cornerEdges cannot
      // silently hide an out-of-range face corner in dev builds.
      if (import.meta.env.DEV && corners[cornerIndex] == null) {
        throw new Error(`buildEdgeToFaces: missing corner ${cornerIndex}`);
      }
    }
  }
  return out;
}

function filterCoplanarBevelEdgesForPreview(
  vertices: Float32Array,
  topology: PolygonTopology,
  selectedEdgeKeys: Set<string>,
): Set<string> {
  if (selectedEdgeKeys.size === 0) return new Set();
  const edgeToFaces = buildEdgeToFaces(topology);
  const faceNormals = computeFaceNormals(vertices, topology);
  const coplanar = new Set<string>();
  const verticesOnNonCoplanar = new Set<number>();

  const status = (key: string): "bevelable" | "coplanar" | "drop" => {
    const faces = edgeToFaces.get(key);
    if (!faces) return "drop";
    if (faces.length !== 2) return faces.length > 2 ? "bevelable" : "drop";
    const f0 = faces[0]!;
    const f1 = faces[1]!;
    const dot =
      faceNormals[f0 * 3]! * faceNormals[f1 * 3]! +
      faceNormals[f0 * 3 + 1]! * faceNormals[f1 * 3 + 1]! +
      faceNormals[f0 * 3 + 2]! * faceNormals[f1 * 3 + 2]!;
    return dot >= 1 - 1e-5 ? "coplanar" : "bevelable";
  };

  for (const key of selectedEdgeKeys) {
    const [aRaw, bRaw] = key.split(",");
    const a = Number(aRaw);
    const b = Number(bRaw);
    const edgeStatus = status(key);
    if (edgeStatus === "bevelable") {
      verticesOnNonCoplanar.add(a);
      verticesOnNonCoplanar.add(b);
    } else if (edgeStatus === "coplanar") {
      coplanar.add(key);
    }
  }

  const out = new Set<string>();
  for (const key of selectedEdgeKeys) {
    const edgeStatus = status(key);
    if (!coplanar.has(key)) {
      if (edgeStatus === "bevelable") out.add(key);
      continue;
    }
    const [aRaw, bRaw] = key.split(",");
    const a = Number(aRaw);
    const b = Number(bRaw);
    if (verticesOnNonCoplanar.has(a) || verticesOnNonCoplanar.has(b)) {
      out.add(key);
    }
  }
  return out;
}

function maxOffsetForCorner(
  vertices: Float32Array,
  v: number,
  prevV: number,
  nextV: number,
  prevBev: boolean,
  nextBev: boolean,
): number {
  const origin = vertexAt(vertices, v);
  const prevRaw = sub3n(vertexAt(vertices, prevV), origin);
  const nextRaw = sub3n(vertexAt(vertices, nextV), origin);
  const prevLen = length3n(prevRaw);
  const nextLen = length3n(nextRaw);
  if (prevLen <= 1e-6 || nextLen <= 1e-6) return 0;

  if (prevBev && !nextBev) return Math.max(0, nextLen * 0.999);
  if (!prevBev && nextBev) return Math.max(0, prevLen * 0.999);

  const prevDir = normalize3n(prevRaw);
  const nextDir = normalize3n(nextRaw);
  const cosFull = Math.max(-1, Math.min(1, dot3n(prevDir, nextDir)));
  const sinHalf = Math.max(1e-6, Math.sqrt((1 - cosFull) * 0.5));
  return Math.max(0, Math.min(prevLen, nextLen) * sinHalf * 0.999);
}

function computeMaxBevelAmountForPreview(
  vertices: Float32Array,
  topology: PolygonTopology,
  selectedFaces: ReadonlySet<number>,
  selectedEdges: ReadonlySet<number>,
): number {
  let minHalf = Number.POSITIVE_INFINITY;
  const { offsets, corners } = topology;

  if (selectedFaces.size > 0) {
    for (const f of selectedFaces) {
      const start = offsets[f]!;
      const size = offsets[f + 1]! - start;
      for (let j = 0; j < size; j++) {
        const v = corners[start + j]!;
        const prevV = corners[start + ((j + size - 1) % size)]!;
        const nextV = corners[start + ((j + 1) % size)]!;
        const maxCorner = maxOffsetForCorner(
          vertices,
          v,
          prevV,
          nextV,
          true,
          true,
        );
        if (maxCorner > 0) minHalf = Math.min(minHalf, maxCorner);
      }
    }
  } else if (selectedEdges.size > 0) {
    const selectedEdgeKeys = new Set<string>();
    for (const edgeIndex of selectedEdges) {
      selectedEdgeKeys.add(topologyEdgeKey(topology, edgeIndex));
    }
    const filtered = filterCoplanarBevelEdgesForPreview(
      vertices,
      topology,
      selectedEdgeKeys,
    );
    const faceCount = offsets.length - 1;
    for (let f = 0; f < faceCount; f++) {
      const start = offsets[f]!;
      const size = offsets[f + 1]! - start;
      for (let j = 0; j < size; j++) {
        const v = corners[start + j]!;
        const prevV = corners[start + ((j + size - 1) % size)]!;
        const nextV = corners[start + ((j + 1) % size)]!;
        const prevBev = filtered.has(edgeKey(prevV, v));
        const nextBev = filtered.has(edgeKey(v, nextV));
        if (!prevBev && !nextBev) continue;
        const maxCorner = maxOffsetForCorner(
          vertices,
          v,
          prevV,
          nextV,
          prevBev,
          nextBev,
        );
        if (maxCorner > 0) minHalf = Math.min(minHalf, maxCorner);
      }
    }
  }

  return Number.isFinite(minHalf) ? minHalf : Number.POSITIVE_INFINITY;
}

function geometryDiagonal(vertices: Float32Array): number {
  if (vertices.length < 3) return 1;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;
  for (let i = 0; i < vertices.length; i += 3) {
    const x = vertices[i]!;
    const y = vertices[i + 1]!;
    const z = vertices[i + 2]!;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    minZ = Math.min(minZ, z);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
    maxZ = Math.max(maxZ, z);
  }
  const dx = maxX - minX;
  const dy = maxY - minY;
  const dz = maxZ - minZ;
  const diagonal = Math.sqrt(dx * dx + dy * dy + dz * dz);
  return diagonal > 1e-12 ? diagonal : 1;
}

function bevelOffsetBasisValue(
  vertices: Float32Array,
  maxOffset: number,
  requestedOffset: number,
): number {
  if (Number.isFinite(maxOffset) && maxOffset > 1e-12) {
    return maxOffset * 0.25;
  }
  if (requestedOffset > 1e-12) return requestedOffset;
  return geometryDiagonal(vertices) * 0.001;
}

function buildExtrudeResult(
  ctx: TopologyStrategyContext,
  target: Extract<
    NonNullable<
      ReturnType<typeof preflightExtrudeSelection> & { ok: true }
    >["target"],
    {
      mode: "faces" | "edges" | "vertices";
    }
  >,
) {
  const result =
    target.mode === "faces"
      ? meshExtrudeFaces(
          ctx.srcVerts,
          ctx.snapObj.indices,
          ctx.getSnapTopology(),
          target.selectedFaces,
        )
      : target.mode === "edges"
        ? meshExtrudeEdges(
            ctx.srcVerts,
            ctx.snapObj.indices,
            ctx.getSnapTopology(),
            target.selectedEdges,
          )
        : meshExtrudeVertices(
            ctx.srcVerts,
            ctx.snapObj.indices,
            ctx.getSnapTopology(),
            target.selectedVertices,
          );
  return result;
}

const extrudeStrategy: TopologyPreviewStrategy = {
  previewCadenceMs: 0,
  execute(ctx) {
    const offset = (ctx.params.offset as number) ?? 0;
    const structuralKey = topologyParamsKey(
      ctx.type,
      ctx.params,
      new Set(["offset"]),
    );
    if (
      ctx.mode === "preview" &&
      sameLinearPreviewSeed(ctx.previewSeed, ctx.type, structuralKey)
    ) {
      return patchLinearPreviewSeed(ctx.em.objectId, ctx.previewSeed, offset);
    }
    const preflight = preflightExtrudeSelection(
      ctx.getSnapTopology(),
      ctx.selection,
    );
    if (!preflight.ok) return preflight;
    const normal = computeSelectionNormal(
      ctx.srcVerts,
      ctx.snapObj.loopNormals,
      ctx.getSnapTopology(),
      preflight.target.selectedVertices,
    );
    return executeSeededDeformationStrategy({
      ...ctx,
      structuralKey,
      offset,
      buildResult: () => buildExtrudeResult(ctx, preflight.target),
      buildSeed: (result, seededPreview) => {
        const directionalData = buildDirectionalPreviewData(
          result.newSelection,
          () => normal,
        );
        return {
          kind: "linearDeformation",
          type: "extrude",
          paramsKey: topologyParamsKey(ctx.type, ctx.params),
          structuralKey,
          vertices: seededPreview.vertices,
          topology: seededPreview.topology,
          indices: seededPreview.indices,
          loopNormals: seededPreview.loopNormals,
          ...(seededPreview.uvs ? { uvs: seededPreview.uvs } : {}),
          selectedVertices: result.newSelection,
          ...(result.newSelectedFaces
            ? { selectedFaces: new Set(result.newSelectedFaces) }
            : {}),
          selectedVertexOrder: directionalData.selectedVertexOrder,
          directionVectors: directionalData.directionVectors,
        };
      },
      applyCommitOffset: (result) => {
        offsetSelectedVertices(
          result.vertices,
          result.newSelection,
          normal,
          offset,
        );
      },
      failureReason:
        "Extrude requires selected vertices, edges, or faces that can form new geometry.",
    });
  },
};

const extrudeAlongNormalsStrategy: TopologyPreviewStrategy = {
  previewCadenceMs: 0,
  execute(ctx) {
    const offset = (ctx.params.offset as number) ?? 0;
    const structuralKey = topologyParamsKey(
      ctx.type,
      ctx.params,
      new Set(["offset"]),
    );
    if (
      ctx.mode === "preview" &&
      sameLinearPreviewSeed(ctx.previewSeed, ctx.type, structuralKey)
    ) {
      return patchLinearPreviewSeed(ctx.em.objectId, ctx.previewSeed, offset);
    }
    const preflight = preflightExtrudeSelection(
      ctx.getSnapTopology(),
      ctx.selection,
    );
    if (!preflight.ok) return preflight;
    return executeSeededDeformationStrategy({
      ...ctx,
      structuralKey,
      offset,
      buildResult: () => buildExtrudeResult(ctx, preflight.target),
      buildSeed: (result, seededPreview) => {
        const vertexNormals = getSnapshotVertexNormals(ctx.snapObj);
        const directionalData = buildDirectionalPreviewData(
          result.newSelection,
          (newV) => {
            const pStart = result.provenance.parentOffsets[newV];
            const pEnd = result.provenance.parentOffsets[newV + 1];
            if (pEnd <= pStart) return null;
            const oldV = result.provenance.parents[pStart];
            return [
              vertexNormals[oldV * 3],
              vertexNormals[oldV * 3 + 1],
              vertexNormals[oldV * 3 + 2],
            ];
          },
        );
        return {
          kind: "linearDeformation",
          type: "extrudeAlongNormals",
          paramsKey: topologyParamsKey(ctx.type, ctx.params),
          structuralKey,
          vertices: seededPreview.vertices,
          topology: seededPreview.topology,
          indices: seededPreview.indices,
          loopNormals: seededPreview.loopNormals,
          ...(seededPreview.uvs ? { uvs: seededPreview.uvs } : {}),
          selectedVertices: result.newSelection,
          ...(result.newSelectedFaces
            ? { selectedFaces: new Set(result.newSelectedFaces) }
            : {}),
          selectedVertexOrder: directionalData.selectedVertexOrder,
          directionVectors: directionalData.directionVectors,
        };
      },
      applyCommitOffset: (result) => {
        offsetAlongVertexNormals(
          result.vertices,
          result.newSelection,
          getSnapshotVertexNormals(ctx.snapObj),
          result.provenance,
          offset,
        );
      },
      failureReason:
        "Extrude Along Normals requires selected vertices, edges, or faces.",
    });
  },
};

const extrudeManifoldStrategy: TopologyPreviewStrategy = {
  previewCadenceMs: 120,
  execute(ctx) {
    const paramsKey = topologyParamsKey(ctx.type, ctx.params);
    const cached = applyReusableStaticPreview(ctx, paramsKey);
    if (cached) return cached;
    const preflight = preflightExtrudeManifold(
      ctx.getSnapTopology(),
      ctx.selection,
    );
    if (!preflight.ok) return preflight;
    const offset = (ctx.params.offset as number) ?? 0;
    const normal = computeSelectionNormal(
      ctx.srcVerts,
      ctx.snapObj.loopNormals,
      ctx.getSnapTopology(),
      preflight.target.selectedVertices,
    );
    const manifoldResult =
      Math.abs(offset) > 1e-7
        ? meshExtrudeManifold(
            ctx.srcVerts,
            ctx.snapObj.indices,
            ctx.getSnapTopology(),
            preflight.target.selectedFaces,
            normal,
            offset,
          )
        : null;
    const result =
      manifoldResult ??
      meshExtrudeFaces(
        ctx.srcVerts,
        ctx.snapObj.indices,
        ctx.getSnapTopology(),
        preflight.target.selectedFaces,
      );
    if (!result) {
      return {
        ok: false,
        reason:
          "Manifold Extrude requires a face selection that can form new geometry.",
      };
    }
    if (!manifoldResult) {
      offsetSelectedVertices(
        result.vertices,
        result.newSelection,
        normal,
        offset,
      );
    }
    return applyCachedTopologyResult(ctx, result, paramsKey);
  },
};

const extrudeIndividualStrategy: TopologyPreviewStrategy = {
  previewCadenceMs: 0,
  execute(ctx) {
    const offset = (ctx.params.offset as number) ?? 0;
    const structuralKey = topologyParamsKey(
      ctx.type,
      ctx.params,
      new Set(["offset"]),
    );
    if (
      ctx.mode === "preview" &&
      sameLinearPreviewSeed(ctx.previewSeed, ctx.type, structuralKey)
    ) {
      return patchLinearPreviewSeed(ctx.em.objectId, ctx.previewSeed, offset);
    }
    const preflight = preflightExtrudeSelection(
      ctx.getSnapTopology(),
      ctx.selection,
    );
    if (!preflight.ok) return preflight;
    if (preflight.target.mode !== "faces") {
      return {
        ok: false,
        reason: "Extrude Individual Faces requires a face selection.",
      };
    }
    const faceTarget = preflight.target;
    return executeSeededDeformationStrategy({
      ...ctx,
      structuralKey,
      offset,
      buildResult: () =>
        meshExtrudeIndividualFaces(
          ctx.srcVerts,
          ctx.snapObj.indices,
          ctx.getSnapTopology(),
          faceTarget.selectedFaces,
        ),
      buildSeed: (result, seededPreview) => {
        const directionalData = buildIndividualExtrudePreviewData(
          seededPreview.vertices,
          seededPreview.topology,
          result.newSelectedFaces!,
          result.newSelection,
        );
        return {
          kind: "linearDeformation",
          type: "extrudeIndividual",
          paramsKey: topologyParamsKey(ctx.type, ctx.params),
          structuralKey,
          vertices: seededPreview.vertices,
          topology: seededPreview.topology,
          indices: seededPreview.indices,
          loopNormals: seededPreview.loopNormals,
          ...(seededPreview.uvs ? { uvs: seededPreview.uvs } : {}),
          selectedVertices: result.newSelection,
          selectedFaces: new Set(result.newSelectedFaces),
          selectedVertexOrder: directionalData.selectedVertexOrder,
          directionVectors: directionalData.directionVectors,
        };
      },
      applyCommitOffset: (result) => {
        offsetIndividualFacesAlongNormals(
          result.vertices,
          result.topology,
          result.newSelectedFaces!,
          result.newSelection,
          offset,
        );
      },
      failureReason: "Extrude Individual Faces requires selected faces.",
    });
  },
};

const subdivideStrategy: TopologyPreviewStrategy = {
  previewCadenceMs: 120,
  execute(ctx) {
    const paramsKey = topologyParamsKey(ctx.type, ctx.params);
    const cached = applyReusableStaticPreview(ctx, paramsKey);
    if (cached) return cached;
    if (ctx.selection.selectedVertices.size === 0) {
      return { ok: false, reason: "Subdivide requires a selection." };
    }
    const cuts = Math.max(1, Math.round((ctx.params.cuts as number) ?? 1));
    const result = executeSubdivideCuts(
      ctx.srcVerts,
      ctx.snapObj.indices,
      ctx.getSnapTopology(),
      new Set(ctx.selection.selectedVertices),
      cuts,
    );
    if (!result) {
      return {
        ok: false,
        reason: "Subdivide could not resolve the current selection.",
      };
    }
    return applyCachedTopologyResult(ctx, result, paramsKey);
  },
};

const insetStrategy: TopologyPreviewStrategy = {
  previewCadenceMs: 0,
  execute(ctx) {
    const structuralKey = topologyParamsKey(
      ctx.type,
      ctx.params,
      new Set(["thickness", "depth"]),
    );
    const thicknessRaw = (ctx.params.thickness as number) ?? 0;
    const values = {
      thickness: thicknessRaw,
      depth: (ctx.params.depth as number) ?? 0,
    };
    if (
      ctx.mode === "preview" &&
      sameAffinePreviewSeed(ctx.previewSeed, ctx.type, structuralKey)
    ) {
      return patchAffinePreviewSeed(ctx.em.objectId, ctx.previewSeed, values);
    }
    const preflight = preflightInsetSelection(
      ctx.getSnapTopology(),
      ctx.selection,
    );
    if (!preflight.ok) return preflight;
    const signedThickness = (ctx.params.outset as boolean)
      ? -thicknessRaw
      : thicknessRaw;

    if (ctx.mode === "preview") {
      const basisThickness = (ctx.params.outset as boolean) ? -1 : 1;
      const baseResult = meshInsetFaces(
        ctx.srcVerts,
        ctx.getSnapTopology(),
        preflight.target.selectedFaces,
        0,
        0,
        (ctx.params.individual as boolean) ?? false,
        (ctx.params.evenOffset as boolean) ?? true,
        (ctx.params.markSharp as boolean) ?? false,
        (ctx.params.markSeam as boolean) ?? false,
      );
      const thicknessBasisResult = meshInsetFaces(
        ctx.srcVerts,
        ctx.getSnapTopology(),
        preflight.target.selectedFaces,
        basisThickness,
        0,
        (ctx.params.individual as boolean) ?? false,
        (ctx.params.evenOffset as boolean) ?? true,
        (ctx.params.markSharp as boolean) ?? false,
        (ctx.params.markSeam as boolean) ?? false,
      );
      const depthBasisResult = meshInsetFaces(
        ctx.srcVerts,
        ctx.getSnapTopology(),
        preflight.target.selectedFaces,
        0,
        1,
        (ctx.params.individual as boolean) ?? false,
        (ctx.params.evenOffset as boolean) ?? true,
        (ctx.params.markSharp as boolean) ?? false,
        (ctx.params.markSeam as boolean) ?? false,
      );
      if (
        baseResult &&
        thicknessBasisResult &&
        depthBasisResult &&
        hasCompatibleAffineResult(baseResult, thicknessBasisResult) &&
        hasCompatibleAffineResult(baseResult, depthBasisResult)
      ) {
        const componentData = buildAffineComponentData(baseResult.vertices, [
          {
            key: "thickness",
            vertices: thicknessBasisResult.vertices,
            basisValue: 1,
          },
          { key: "depth", vertices: depthBasisResult.vertices, basisValue: 1 },
        ]);
        if (componentData) {
          const previewObj = applyResult(ctx.obj, baseResult, ctx.type);
          const seed: AffineTopologyPreviewSeed = {
            kind: "affineDeformation",
            type: ctx.type,
            paramsKey: topologyParamsKey(ctx.type, ctx.params),
            structuralKey,
            vertices: previewObj.vertices,
            topology: previewObj.topology,
            indices: previewObj.indices,
            loopNormals: previewObj.loopNormals,
            ...(previewObj.uvs ? { uvs: previewObj.uvs } : {}),
            selectedVertices: baseResult.newSelection,
            ...(baseResult.newSelectedFaces
              ? { selectedFaces: new Set(baseResult.newSelectedFaces) }
              : {}),
            ...componentData,
          };
          applyAffinePreviewSeed(seed, values, ctx.em, ctx.set_);
          return { ok: true, previewSeed: seed };
        }
      }
    }

    const result = meshInsetFaces(
      ctx.srcVerts,
      ctx.getSnapTopology(),
      preflight.target.selectedFaces,
      signedThickness,
      (ctx.params.depth as number) ?? 0,
      (ctx.params.individual as boolean) ?? false,
      (ctx.params.evenOffset as boolean) ?? true,
      (ctx.params.markSharp as boolean) ?? false,
      (ctx.params.markSeam as boolean) ?? false,
    );
    if (!result) {
      return {
        ok: false,
        reason: "Inset Faces requires a face-backed selection.",
      };
    }
    return applyCachedTopologyResult(
      ctx,
      result,
      topologyParamsKey(ctx.type, ctx.params),
    );
  },
};

const bevelStrategy: TopologyPreviewStrategy = {
  previewCadenceMs: 0,
  execute(ctx) {
    const structuralKey = topologyParamsKey(
      ctx.type,
      ctx.params,
      new Set(["offset"]),
    );
    const requestedOffset = Math.max(0, (ctx.params.offset as number) ?? 0);
    if (
      ctx.mode === "preview" &&
      sameAffinePreviewSeed(ctx.previewSeed, ctx.type, structuralKey)
    ) {
      const maxOffset =
        ctx.previewSeed.maxValuesByParam?.offset ?? Number.POSITIVE_INFINITY;
      return patchAffinePreviewSeed(ctx.em.objectId, ctx.previewSeed, {
        offset: Math.min(requestedOffset, maxOffset),
      });
    }
    const preflight = preflightBevelSelection(
      ctx.getSnapTopology(),
      ctx.selection,
    );
    if (!preflight.ok) return preflight;
    const maxOffset =
      ((ctx.params.clampOverlap as boolean) ?? true)
        ? computeMaxBevelAmountForPreview(
            ctx.srcVerts,
            ctx.getSnapTopology(),
            preflight.target.selectedFaces,
            preflight.target.selectedEdges,
          )
        : Number.POSITIVE_INFINITY;
    const effectiveOffset = Math.min(requestedOffset, maxOffset);
    const values = { offset: effectiveOffset };

    if (ctx.mode === "preview") {
      const basisOffset = bevelOffsetBasisValue(
        ctx.srcVerts,
        maxOffset,
        requestedOffset,
      );
      const upperBasisOffset =
        Number.isFinite(maxOffset) && maxOffset > basisOffset
          ? Math.min(maxOffset * 0.5, basisOffset * 2)
          : basisOffset * 2;
      const baseResult = meshBevelGeometry(
        ctx.srcVerts,
        ctx.getSnapTopology(),
        preflight.target.selectedFaces,
        preflight.target.selectedEdges,
        basisOffset,
        Math.max(1, Math.round((ctx.params.segments as number) ?? 1)),
        (ctx.params.profile as number) ?? 0.5,
        false,
        (ctx.params.markSharp as boolean) ?? true,
        (ctx.params.markSeam as boolean) ?? false,
      );
      const offsetBasisResult = meshBevelGeometry(
        ctx.srcVerts,
        ctx.getSnapTopology(),
        preflight.target.selectedFaces,
        preflight.target.selectedEdges,
        upperBasisOffset,
        Math.max(1, Math.round((ctx.params.segments as number) ?? 1)),
        (ctx.params.profile as number) ?? 0.5,
        false,
        (ctx.params.markSharp as boolean) ?? true,
        (ctx.params.markSeam as boolean) ?? false,
      );
      if (
        baseResult &&
        offsetBasisResult &&
        hasCompatibleAffineResult(baseResult, offsetBasisResult)
      ) {
        const componentData = buildAffineComponentData(baseResult.vertices, [
          {
            key: "offset",
            vertices: offsetBasisResult.vertices,
            basisValue: upperBasisOffset - basisOffset,
          },
        ]);
        if (componentData) {
          const previewObj = applyResult(ctx.obj, baseResult, ctx.type);
          const seed: AffineTopologyPreviewSeed = {
            kind: "affineDeformation",
            type: ctx.type,
            paramsKey: topologyParamsKey(ctx.type, ctx.params),
            structuralKey,
            vertices: previewObj.vertices,
            topology: previewObj.topology,
            indices: previewObj.indices,
            loopNormals: previewObj.loopNormals,
            ...(previewObj.uvs ? { uvs: previewObj.uvs } : {}),
            baseValuesByParam: { offset: basisOffset },
            maxValuesByParam: { offset: maxOffset },
            selectedVertices: baseResult.newSelection,
            ...(baseResult.newSelectedFaces
              ? { selectedFaces: new Set(baseResult.newSelectedFaces) }
              : {}),
            ...componentData,
          };
          applyAffinePreviewSeed(seed, values, ctx.em, ctx.set_);
          return { ok: true, previewSeed: seed };
        }
      }
    }

    const result = meshBevelGeometry(
      ctx.srcVerts,
      ctx.getSnapTopology(),
      preflight.target.selectedFaces,
      preflight.target.selectedEdges,
      requestedOffset,
      Math.max(1, Math.round((ctx.params.segments as number) ?? 1)),
      (ctx.params.profile as number) ?? 0.5,
      (ctx.params.clampOverlap as boolean) ?? true,
      (ctx.params.markSharp as boolean) ?? true,
      (ctx.params.markSeam as boolean) ?? false,
    );
    if (!result) {
      return { ok: false, reason: "Bevel requires selected edges or faces." };
    }
    return applyCachedTopologyResult(
      ctx,
      result,
      topologyParamsKey(ctx.type, ctx.params),
    );
  },
};

const bridgeEdgeLoopsStrategy: TopologyPreviewStrategy = {
  previewCadenceMs: 120,
  execute(ctx) {
    const paramsKey = topologyParamsKey(ctx.type, ctx.params);
    const cached = applyReusableStaticPreview(ctx, paramsKey);
    if (cached) return cached;
    const preflight = preflightBridgeEdgeLoops(
      ctx.getSnapTopology(),
      ctx.selection,
    );
    if (!preflight.ok) return preflight;
    const result = meshBridgeEdgeLoops(
      ctx.srcVerts,
      ctx.snapObj.indices,
      ctx.getSnapTopology(),
      preflight.target.selectedEdges,
      Math.round((ctx.params.twist as number) ?? 0),
      (ctx.params.reverse as boolean) ?? false,
      resolveSelectedEdgeSeed(ctx.getSnapTopology(), ctx.selection),
    );
    if (!result) {
      return {
        ok: false,
        reason:
          "Bridge Edge Loops requires exactly two closed boundary loops of equal length.",
      };
    }
    return applyCachedTopologyResult(ctx, result, paramsKey);
  },
};

const gridFillStrategy: TopologyPreviewStrategy = {
  previewCadenceMs: 120,
  execute(ctx) {
    const paramsKey = topologyParamsKey(ctx.type, ctx.params);
    const cached = applyReusableStaticPreview(ctx, paramsKey);
    if (cached) return cached;
    const preflight = preflightGridFillSelection(
      ctx.getSnapTopology(),
      ctx.selection,
    );
    if (!preflight.ok) return preflight;
    const result = meshGridFill(
      ctx.srcVerts,
      ctx.snapObj.indices,
      ctx.getSnapTopology(),
      preflight.target.selectedVertices,
      Math.max(1, Math.round((ctx.params.span as number) ?? 1)),
      Math.round((ctx.params.offset as number) ?? 0),
      resolveBoundarySeedVertex(ctx.getSnapTopology(), ctx.selection),
    );
    if (!result) {
      return {
        ok: false,
        reason: "Grid Fill requires one clean closed boundary loop.",
      };
    }
    return applyCachedTopologyResult(ctx, result, paramsKey);
  },
};

const mergeByDistanceStrategy: TopologyPreviewStrategy = {
  previewCadenceMs: 120,
  execute(ctx) {
    const paramsKey = topologyParamsKey(ctx.type, ctx.params);
    const cached = applyReusableStaticPreview(ctx, paramsKey);
    if (cached) return cached;
    if (ctx.selection.selectedVertices.size < 2) {
      return {
        ok: false,
        reason: "Merge by Distance requires at least 2 selected vertices.",
      };
    }
    const result = meshMergeByDistance(
      new Float32Array(ctx.srcVerts),
      ctx.snapObj.indices,
      ctx.getSnapTopology(),
      new Set(ctx.selection.selectedVertices),
      (ctx.params.threshold as number) ?? 0.01,
    );
    if (!result) {
      return {
        ok: false,
        reason:
          "Merge by Distance requires a positive threshold that collapses at least one vertex pair.",
      };
    }
    return applyCachedTopologyResult(ctx, result, paramsKey);
  },
};

const edgeLoopInsertStrategy: TopologyPreviewStrategy = {
  previewCadenceMs: 120,
  execute(ctx) {
    const paramsKey = topologyParamsKey(ctx.type, ctx.params);
    const cached = applyReusableStaticPreview(ctx, paramsKey);
    if (cached) return cached;
    if (!ctx.frozen.edgeLoopInsertEdge) {
      return { ok: false, reason: "Edge Loop Insert requires a seeded edge." };
    }
    const seedPreflight = preflightEdgeLoopInsertSeed(
      ctx.getSnapTopology(),
      ctx.frozen.edgeLoopInsertEdge[0],
      ctx.frozen.edgeLoopInsertEdge[1],
    );
    if (!seedPreflight.ok) return seedPreflight;
    const [v0, v1] = seedPreflight.target.seedEdge;
    const result = meshInsertEdgeLoop(
      ctx.srcVerts,
      ctx.snapObj.indices,
      ctx.getSnapTopology(),
      v0,
      v1,
      ctx.params.position as number,
      Math.max(1, Math.round((ctx.params.cuts as number) ?? 1)),
    );
    if (!result) {
      return {
        ok: false,
        reason: "Edge Loop Insert requires an edge on a cuttable ring.",
      };
    }
    return applyCachedTopologyResult(ctx, result, paramsKey);
  },
};

const TOPOLOGY_PREVIEW_STRATEGIES: Partial<
  Record<CommandType, TopologyPreviewStrategy>
> = {
  extrude: extrudeStrategy,
  extrudeAlongNormals: extrudeAlongNormalsStrategy,
  extrudeManifold: extrudeManifoldStrategy,
  extrudeIndividual: extrudeIndividualStrategy,
  subdivide: subdivideStrategy,
  inset: insetStrategy,
  bevel: bevelStrategy,
  bridgeEdgeLoops: bridgeEdgeLoopsStrategy,
  gridFill: gridFillStrategy,
  mergeByDistance: mergeByDistanceStrategy,
  edgeLoopInsert: edgeLoopInsertStrategy,
};

export function getTopologyPreviewCadenceMs(type: CommandType): number {
  return TOPOLOGY_PREVIEW_STRATEGIES[type]?.previewCadenceMs ?? 0;
}

export function executeTopologyOp(
  type: CommandType,
  params: Record<string, number | boolean | string>,
  srcVerts: Float32Array,
  snapObj: SnapObj,
  em: NonNullable<AppState["editMode"]>,
  obj: SceneObject,
  state: AppState,
  set_: Set_,
  frozen: FrozenTopologySelectionState,
  mode: "preview" | "commit" = "commit",
  previewSeed: TopologyPreviewSeed | null = null,
): TopologyExecutionResult {
  if (!usesTopologyPreview(type)) {
    return {
      ok: false,
      reason: `Unsupported topology command type "${type}".`,
    };
  }
  const strategy = TOPOLOGY_PREVIEW_STRATEGIES[type];
  if (!strategy) {
    return {
      ok: false,
      reason: `Unsupported topology command type "${type}".`,
    };
  }
  let snapTopology: PolygonTopology | null = null;
  const getSnapTopology = () => {
    if (!snapTopology) snapTopology = snapToTopology(snapObj);
    return snapTopology;
  };
  return strategy.execute({
    type,
    params,
    srcVerts,
    snapObj,
    em,
    obj,
    state,
    set_,
    frozen,
    mode,
    previewSeed,
    getSnapTopology,
    selection: buildSelectionInput(em, frozen),
  });
}
